import React from 'react';

import { openReactActionDialog, ReactActionDialogBodyProps } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { DomainUpdateType, IUpdateBaasEdgePoolResult } from '~src/settings/dataconnect-settings/serverApi/requests/UpdateBaasEdgePoolRequest';
import { BaasEdgePoolAndCluster } from '~src/settings/dataconnect-settings/serverApi/resources/BaasEdgePoolAndCluster';

import { Domain, DomainID } from '../../types/Domain';
import { DomainPermission } from '../../types/DomainPermission';
import { DomainTableItem } from '../../types/DomainTableItem';
import { DomainValidationStatus } from '../../types/DomainValidationStatus';
import { PoolID } from '../../types/PoolID';
import { UserDefinedPoolModel } from '../../types/PoolModel';
import { createDomainTableItems, validateDomainName } from '../../utils/DomainUtilities';
import { getGeneralErrorMessage } from '../../utils/EdgePoolErrorUtilities';
import { AddNewDomainDialogBody } from './AddNewDomainDialogBody';


export function openAddNewDomainDialog(
  onAddDomainDialogSave: (domainName: string, domainPermission: DomainPermission) => void,
  otherDomainNames: ReadonlyArray<string>,
  poolDomainNames: ReadonlyArray<string>,
  poolID: PoolID,
  poolName: string
) {
  const action = (_props: AddNewDomainDialogInputProps, state: AddNewDomainDialogState) => {
    onAddDomainDialogSave(state.domainName, state.domainPermission);
    return Promise.resolve();
  };
  const dialogInstance = openReactActionDialog({
    title: Messages.dataConnectSiteSettingsAddNewDomain({POOL_NAME: poolName}),
    bodyComponent: AddNewDomainDialog,
    initialBodyProps: { otherDomainNames, poolDomainNames, poolID, poolName},
    initialDialogState: { domainName: '',  domainPermission: DomainPermission.ALLOW, domainValidationStatus: DomainValidationStatus.NO_ERROR },
    actionDisabled: (_props, state) => {
      return state.domainValidationStatus !== DomainValidationStatus.NO_ERROR;
    },
    actionName: Messages.save(),
    action: action,
    isActionDelete: false,
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: false
  });

  return Promise.resolve(dialogInstance.result);
}

export type AddNewDomainDialogInputProps = Readonly<{
  otherDomainNames: ReadonlyArray<string>;
  poolDomainNames: ReadonlyArray<string>;
  poolID: PoolID;
  poolName: string;
}>;

export type AddNewDomainDialogState = Readonly<{
  domainName: string;
  domainPermission: DomainPermission;
  domainValidationStatus: DomainValidationStatus;
}>;

export type AddNewDomainDialogProps = ReactActionDialogBodyProps<
  AddNewDomainDialogInputProps,
  AddNewDomainDialogState
>;

export const AddNewDomainDialog = (props: AddNewDomainDialogProps): JSX.Element => {
  const setDomainValidationStatus = (validationStatus: DomainValidationStatus) => props.updateDialogState({ domainValidationStatus: validationStatus });
  const setDomainName = (changedDomainName: string) => props.updateDialogState({ domainName: changedDomainName });
  const setDomainPermission = (changedDomainPermission: DomainPermission) => props.updateDialogState({ domainPermission: changedDomainPermission });

  const onDomainNameChange = (changedDomainName: string) => {
    setDomainValidationStatus(validateDomainName(changedDomainName, props.otherDomainNames, props.poolDomainNames));
    setDomainName(changedDomainName);
  };
  const onDomainPermissionChange = (changedDomainPermission: DomainPermission) => {
    setDomainPermission(changedDomainPermission);
  };

  return (
    <AddNewDomainDialogBody
      onDomainNameChange={onDomainNameChange}
      domainName={props.dialogState.domainName}
      onDomainPermissionChange={onDomainPermissionChange}
      domainPermission={props.dialogState.domainPermission}
      assignedPoolID={props.poolID}
      assignedPoolName={props.poolName}
      domainValidationStatus={props.dialogState.domainValidationStatus}
    />
  );
};


export function openAddPoolDomainDialog(poolId: PoolID, poolName: string, siteId: string, userDefinedPools: Record<string, UserDefinedPoolModel>, bridgeDomains: Record<string, Domain>, refreshPoolsAndClusters: () => void) {

  const onAddDomainDialogSave = (domainName: string) => {
    BaasEdgePoolAndCluster.updateBaasPool({
      poolId: poolId,
      displayName: null,
      domainChange: { change: DomainUpdateType.Add, domainId: domainName as DomainID },
      siteId: siteId,
      DataConnectSettingsVersion: '0'
    })
      .then((result: IUpdateBaasEdgePoolResult) => {
        refreshPoolsAndClusters();
        if (result.success) {
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.dataConnectSiteSettingsAddDomainSuccess() });
        }
        if (result.errors) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
          });
        }
      });
  };

  const domainTableItems: ReadonlyArray<DomainTableItem> = createDomainTableItems(userDefinedPools, {});
  const bridgeDomainNames: ReadonlyArray<string> = Object.keys(bridgeDomains);
  openAddNewDomainDialog(onAddDomainDialogSave,
    domainTableItems.map((item: DomainTableItem) => item.domainName), bridgeDomainNames, poolId, poolName);
}

